import { getAccessToken } from "@privy-io/react-auth";

import { GLOBAL_API_URL } from "./config";

type ApiEnvelope<T> = {
  data: T;
  error: string | null;
  statusCode?: number;
};

export type Partner = {
  id: string;
  partnerCode: string;
  name: string;
  status: "active" | "inactive";
  createdAt: string;
  updatedAt: string;
};

export type PartnerManager = {
  id: string;
  partnerId: string;
  accountId: string;
  createdAt: string;
};

export type PartnerMeResponse = {
  partner: Partner;
  manager: PartnerManager;
};

export type SignupByUtm = {
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmContent: string | null;
  utmTerm: string | null;
  signups: number;
};

export type PartnerDashboardResponse = {
  partner: Partner;
  totalStarted: number;
  totalSignups: number;
  signupsByUtm: SignupByUtm[];
};

export type PartnerSignup = {
  id: string;
  sport: string;
  partnerCode: string;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmContent: string | null;
  utmTerm: string | null;
  deviceType: string;
  landingPath: string;
  identifiedAt: string | null;
  redeemedAt: string | null;
};

export type PartnerSignupsResponse = {
  partner: Partner;
  signups: PartnerSignup[];
  total: number;
  offset: number;
  limit: number;
};

export type PartnerDashboardFilters = {
  utmSource?: string[];
  utmMedium?: string[];
  utmCampaign?: string[];
  utmContent?: string[];
  utmTerm?: string[];
};

const buildUrl = (path: string) => `${GLOBAL_API_URL}/v1${path}`;

const buildQueryString = (
  params: Record<string, string | number | string[] | undefined>,
) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value
        .filter((item) => item !== "")
        .forEach((item) => query.append(key, item));
      return;
    }

    if (value !== undefined && value !== "") {
      query.set(key, String(value));
    }
  });
  const value = query.toString();
  return value ? `?${value}` : "";
};

async function apiGet<T>(path: string): Promise<T> {
  const token = await getAccessToken();
  const result = await fetch(buildUrl(path), {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  });

  const body = (await result.json()) as ApiEnvelope<T>;
  if (!result.ok) {
    throw new Error(body.error || `Request failed with ${result.status}`);
  }

  return body.data;
}

export const partnersApi = {
  getMe: () => apiGet<PartnerMeResponse>("/partners/me"),
  getDashboard: (filters: PartnerDashboardFilters = {}) =>
    apiGet<PartnerDashboardResponse>(
      `/partners/dashboard${buildQueryString(filters)}`,
    ),
  getSignups: (filters: PartnerDashboardFilters = {}) =>
    apiGet<PartnerSignupsResponse>(
      `/partners/signups${buildQueryString({ ...filters, limit: 50 })}`,
    ),
};
